/**
 * Text measurement and node sizing.
 *
 * Measurement is done with a character-class width table rather than the
 * browser's `canvas.measureText`. That is a deliberate trade: canvas metrics
 * depend on the installed fonts, so the same document would lay out differently
 * on Windows, macOS and CI, and the exported SVG would not match the screen.
 * An approximation that is identical everywhere is worth more here than one
 * that is exact on one machine — layout is deterministic by construction.
 *
 * The table is calibrated against Inter at 14px; other UI-sans faces are close
 * enough that the padding absorbs the difference.
 */
import type { NodeRole, NodeShape, Size } from './types';

/** Width of a character as a fraction of the font size. */
function charWidthRatio(code: number): number {
  // Latin-1 fast path.
  if (code < 0x2e80) {
    switch (code) {
      case 0x20: // space
        return 0.26;
      case 0x69: // i
      case 0x6a: // j
      case 0x6c: // l
      case 0x49: // I
      case 0x21: // !
      case 0x2e: // .
      case 0x2c: // ,
      case 0x3a: // :
      case 0x3b: // ;
      case 0x27: // '
      case 0x7c: // |
        return 0.28;
      case 0x66: // f
      case 0x74: // t
      case 0x72: // r
      case 0x28:
      case 0x29:
      case 0x5b:
      case 0x5d:
        return 0.36;
      case 0x6d: // m
      case 0x77: // w
        return 0.86;
      case 0x4d: // M
      case 0x57: // W
      case 0x40: // @
        return 0.92;
      default:
        break;
    }
    if (code >= 0x41 && code <= 0x5a) return 0.68; // A-Z
    if (code >= 0x30 && code <= 0x39) return 0.57; // 0-9
    if (code >= 0x61 && code <= 0x7a) return 0.55; // a-z
    return 0.55;
  }
  // CJK, Hangul and fullwidth forms are square; so are most emoji.
  return 1.0;
}

/** Approximate rendered width of `text` at `fontSize`, in pixels. */
export function measureText(text: string, fontSize: number, bold = false): number {
  let ratio = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    ratio += charWidthRatio(code);
  }
  return ratio * fontSize * (bold ? 1.05 : 1);
}

export interface WrapOptions {
  readonly fontSize: number;
  readonly maxWidth: number;
  readonly maxLines: number;
  readonly bold: boolean;
}

export const DEFAULT_WRAP: WrapOptions = {
  fontSize: 14,
  maxWidth: 220,
  maxLines: 4,
  bold: false,
};

/**
 * Greedy word wrap. Words longer than the line are hard-broken so a single
 * pasted URL cannot blow the node out to a thousand pixels.
 */
export function wrapText(text: string, options: Partial<WrapOptions> = {}): string[] {
  const opts = { ...DEFAULT_WRAP, ...options };
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized === '') return [''];

  const words = normalized.split(' ');
  const lines: string[] = [];
  let current = '';

  const push = (line: string): void => {
    if (lines.length < opts.maxLines) lines.push(line);
  };

  for (const word of words) {
    const candidate = current === '' ? word : `${current} ${word}`;
    if (measureText(candidate, opts.fontSize, opts.bold) <= opts.maxWidth) {
      current = candidate;
      continue;
    }

    if (current !== '') {
      push(current);
      current = '';
      if (lines.length >= opts.maxLines) break;
    }

    if (measureText(word, opts.fontSize, opts.bold) <= opts.maxWidth) {
      current = word;
      continue;
    }

    // Hard-break an over-long word.
    let chunk = '';
    for (const char of word) {
      const next = chunk + char;
      if (measureText(next, opts.fontSize, opts.bold) > opts.maxWidth && chunk !== '') {
        push(chunk);
        chunk = char;
        if (lines.length >= opts.maxLines) break;
      } else {
        chunk = next;
      }
    }
    current = chunk;
    if (lines.length >= opts.maxLines) break;
  }

  if (current !== '' && lines.length < opts.maxLines) push(current);
  if (lines.length === 0) push(normalized.slice(0, 40));

  // Signal truncation rather than silently dropping words.
  const consumed = lines.join(' ');
  if (consumed.length < normalized.length && lines.length === opts.maxLines) {
    const last = lines[lines.length - 1]!;
    lines[lines.length - 1] = `${last.replace(/\s*\S*$/, '')}…`.replace(/^…$/, `${last}…`);
  }

  return lines;
}

/* ------------------------------------------------------------------ *
 * Node sizing
 * ------------------------------------------------------------------ */

export interface SizingOptions {
  readonly fontSize: number;
  readonly lineHeight: number;
  readonly paddingX: number;
  readonly paddingY: number;
  readonly minWidth: number;
  readonly minHeight: number;
  readonly maxTextWidth: number;
  readonly maxLines: number;
  /** Extra width reserved for a metadata icon. */
  readonly iconWidth: number;
}

export const DEFAULT_SIZING: SizingOptions = {
  fontSize: 14,
  lineHeight: 19,
  paddingX: 18,
  paddingY: 12,
  minWidth: 96,
  minHeight: 44,
  maxTextWidth: 224,
  maxLines: 4,
  iconWidth: 22,
};

export interface MeasuredLabel {
  readonly lines: readonly string[];
  readonly size: Size;
}

/**
 * Wraps a label and returns the box the shape needs.
 *
 * Diamonds get roughly 1.6x the text box because a rhombus only offers about
 * 40% of its bounding box to a centred line of text; terminators and junctions
 * have their own rules.
 */
export function measureNode(
  label: string,
  role: NodeRole,
  shape: NodeShape,
  hasIcon: boolean,
  options: Partial<SizingOptions> = {},
): MeasuredLabel {
  const opts = { ...DEFAULT_SIZING, ...options };

  if (shape === 'junction') {
    return { lines: [], size: { width: 12, height: 12 } };
  }

  const textLimit = shape === 'decision' ? opts.maxTextWidth * 0.82 : opts.maxTextWidth;
  const bold = role === 'start' || role === 'end';
  const lines = wrapText(label, {
    fontSize: opts.fontSize,
    maxWidth: textLimit,
    maxLines: opts.maxLines,
    bold,
  });

  const textWidth = Math.max(...lines.map((line) => measureText(line, opts.fontSize, bold)), 0);
  const textHeight = lines.length * opts.lineHeight;

  let width = textWidth + opts.paddingX * 2 + (hasIcon ? opts.iconWidth : 0);
  let height = textHeight + opts.paddingY * 2;

  switch (shape) {
    case 'decision':
      // A diamond must grow in both axes to keep the text off the slanted edges.
      width = textWidth * 1.65 + opts.paddingX * 2 + (hasIcon ? opts.iconWidth : 0);
      height = Math.max(textHeight * 1.9 + opts.paddingY, 64);
      break;
    case 'terminator':
      width += 16;
      height = Math.max(height, 46);
      break;
    case 'io':
      // Parallelogram slant eats horizontal room on both sides.
      width += 26;
      break;
    case 'subprocess':
      // Double side bars.
      width += 18;
      break;
    case 'note':
      width += 12;
      break;
    case 'process':
    case 'error':
      break;
  }

  return {
    lines,
    size: {
      width: Math.round(Math.max(opts.minWidth, width)),
      height: Math.round(Math.max(opts.minHeight, height)),
    },
  };
}
